import { jsonBody, unwrap } from "./foyer";

export interface DemoSessionSummary {
  id: string;
  sourceUrl: string;
  createdBy: string;
  premiseSlug: string | null;
  running: boolean;
  updatedAt: string;
  lastAppliedAt: string | null;
}

export interface DemoChatEntry {
  role: "user" | "assistant";
  text: string;
  at: string;
  stop?: "done" | "round_limit" | "refused" | "error";
}

export interface DemoSessionView {
  id: string;
  sourceUrl: string;
  createdBy: string;
  premiseSlug: string | null;
  running: boolean;
  chat: DemoChatEntry[];
  lastAppliedAt: string | null;
  presenterRoom: string | null;
  firstRoom: string | null;
  signageScreens: Array<{ id: string; location: string }>;
  specProblems: string[];
  canUndo: boolean;
}

/** One line of progress while Claude works. */
export type DemoTurnEvent =
  | { event: "text"; data: { text: string } }
  | { event: "web"; data: { kind: "search" | "fetch"; detail: string } }
  | { event: "tool_call"; data: { name: string } }
  | {
      event: "tool_result";
      data: { name: string; ok: boolean; content: string };
    }
  | {
      event: "result";
      data: {
        finalText: string;
        stop: string;
        premiseSlug: string | null;
        applied: boolean;
        specProblems: string[];
      };
    }
  | { event: "error"; data: { error: string; message: string } };

export const demoStudioApi = {
  availability(): Promise<{ available: boolean; reason?: string }> {
    return unwrap(fetch("/api/cms/v1/demo-studio/availability"));
  },
  list(): Promise<DemoSessionSummary[]> {
    return unwrap(fetch("/api/cms/v1/demo-studio/sessions"));
  },
  get(id: string): Promise<DemoSessionView> {
    return unwrap(
      fetch(`/api/cms/v1/demo-studio/sessions/${encodeURIComponent(id)}`)
    );
  },
  create(url: string): Promise<{ id: string }> {
    return unwrap(
      fetch("/api/cms/v1/demo-studio/sessions", jsonBody("POST", { url }))
    );
  },
  undo(
    id: string
  ): Promise<{ restored: boolean; applied: boolean; message: string }> {
    return unwrap(
      fetch(
        `/api/cms/v1/demo-studio/sessions/${encodeURIComponent(id)}/undo`,
        jsonBody("POST", {})
      )
    );
  },

  /** Delete a demo - everything it owns, the prospect's pictures included. */
  deleteDemo(premiseId: string): Promise<{ assetsDeleted: number }> {
    return unwrap(
      fetch(`/api/cms/v1/demos/${encodeURIComponent(premiseId)}`, {
        method: "DELETE",
      })
    );
  },

  /** Copy a demo into a new premise under a customer's company. */
  cloneDemo(
    premiseId: string,
    target: { companyId: string; slug: string; name?: string }
  ): Promise<{ premiseId: string }> {
    return unwrap(
      fetch(
        `/api/cms/v1/demos/${encodeURIComponent(premiseId)}/clone`,
        jsonBody("POST", target)
      )
    );
  },

  /**
   * Run one turn and stream its progress. Resolves when the stream ends. If
   * the connection drops, the turn still finishes on the server - reload the
   * session to see where it got to.
   */
  async turn(
    id: string,
    body: { message?: string; note?: string },
    onEvent: (e: DemoTurnEvent) => void
  ): Promise<void> {
    const res = await fetch(
      `/api/cms/v1/demo-studio/sessions/${encodeURIComponent(id)}/turns`,
      jsonBody("POST", body)
    );
    if (!res.ok || !res.body) {
      await unwrap(Promise.resolve(res));
      return;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let cut: number;
      while ((cut = buffer.indexOf("\n\n")) >= 0) {
        const block = buffer.slice(0, cut);
        buffer = buffer.slice(cut + 2);
        const event = /^event: (.+)$/m.exec(block)?.[1];
        const data = /^data: (.+)$/m.exec(block)?.[1];
        if (event && data) {
          try {
            onEvent({ event, data: JSON.parse(data) } as DemoTurnEvent);
          } catch {
            /* a malformed line is skipped, not fatal */
          }
        }
      }
    }
  },
};
